import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import React from 'react';
import wrapColorTheme from '../lib/WrapColorTheme';
import { colors, styled } from '../Stitches';

const SCROLLBAR_SIZE = 8;

const StyledScrollArea = styled(ScrollAreaPrimitive.Root, {
  overflow: 'hidden',
  height: '100%',
});

const StyledViewport = styled(ScrollAreaPrimitive.Viewport, {
  width: '100%',
  height: '100%',
  borderRadius: 'inherit',
  padding: '$6',
});

const StyledScrollbarPrimitive = styled(ScrollAreaPrimitive.Scrollbar, {
  display: 'flex',
  // ensures no selection
  userSelect: 'none',
  // disable browser handling of all panning and zooming gestures on touch devices
  touchAction: 'none',
  padding: 1,
  transition: 'background 120ms ease-out',

  '&[data-orientation="vertical"]': { width: SCROLLBAR_SIZE },
  '&[data-orientation="horizontal"]': {
    flexDirection: 'column',
    height: SCROLLBAR_SIZE,
  },

  borderRadius: SCROLLBAR_SIZE,

  variants: {
    theme: {
      light: { background: colors.gray200 },
      dark: { background: colors.dark.whiteAlpha100 },
    },
  },
});

const StyledScrollbar = wrapColorTheme(StyledScrollbarPrimitive);

const StyledThumbPrimitive = styled(ScrollAreaPrimitive.Thumb, {
  flex: 1,
  borderRadius: SCROLLBAR_SIZE,
  // increase target size for touch devices https://www.w3.org/WAI/WCAG21/Understanding/target-size.html
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: '100%',
    minWidth: 44,
    minHeight: 44,
  },
  transition: 'background 120ms ease-out',

  variants: {
    theme: {
      light: {
        background: colors.gray600,
        '&:hover': { background: colors.gray800 },
      },
      dark: {
        background: colors.dark.whiteAlpha400,
        '&:hover': { background: colors.gray100 },
      },
    },
  },
});

const StyledThumb = wrapColorTheme(StyledThumbPrimitive);

const StyledCorner = styled(ScrollAreaPrimitive.Corner, {});

// Exports
export const ScrollArea = StyledScrollArea;
export const ScrollAreaViewport = StyledViewport;
export const ScrollAreaScrollbar = StyledScrollbar;
export const ScrollAreaThumb = StyledThumb;
export const ScrollAreaCorner = StyledCorner;

export const LetterScrollArea = ({ children }: { children: React.ReactNode }) => (
  <ScrollArea type="auto" scrollHideDelay={600}>
    <ScrollAreaViewport>{children}</ScrollAreaViewport>
    <ScrollAreaScrollbar orientation="vertical">
      <ScrollAreaThumb />
    </ScrollAreaScrollbar>
    <ScrollAreaScrollbar orientation="horizontal">
      <ScrollAreaThumb />
    </ScrollAreaScrollbar>
    <ScrollAreaCorner />
  </ScrollArea>
);

export default LetterScrollArea;
